import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { PageSeo } from './PageSeo';
import { BackToToday, PageHeader } from './SiteChrome';
import { ContentLayout } from './PageSidebar';
import { SiteFooter } from './SiteFooter';
import { PAGE_META } from '../lib/seo';
import { markNewsSeen, NEWS_ITEMS } from '../lib/news';

export function NewsPage() {
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);

  useEffect(() => {
    markNewsSeen();
    setNewsUnreadCount(0);
  }, []);

  return (
    <div className="relative pb-8">
      <PageSeo meta={PAGE_META.news} />
      <PageHeader newsUnreadCount={newsUnreadCount} />

      <ContentLayout current="news">
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="mb-8"
      >
        <BackToToday />
        <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-3">News</h1>
        <p className="text-foreground/60 text-sm md:text-base leading-relaxed max-w-md">
          What’s new in Centumth — features, fixes, and the little things that make the game better.
        </p>
      </motion.div>

      <div className="relative">
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-border" aria-hidden />

        <div className="space-y-5">
          {NEWS_ITEMS.map((item, index) => (
            <motion.article
              key={item.id}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 + index * 0.05, ease: 'easeOut' }}
              className="relative pl-10"
            >
              <div
                className={`absolute left-0 top-5 h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                  item.isNew
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background'
                }`}
              >
                <Sparkles size={12} className={item.isNew ? '' : 'text-foreground/40'} />
              </div>

              <div
                className={`rounded-3xl border px-5 py-5 ${
                  item.isNew
                    ? 'border-foreground/20 bg-muted/40 shadow-sm'
                    : 'border-border bg-background'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
                    {item.dateLabel}
                  </div>
                  {item.isNew && (
                    <div className="text-[10px] font-black uppercase tracking-widest bg-red-500 text-white px-2 py-0.5 rounded-full">
                      New
                    </div>
                  )}
                </div>

                <h2 className="text-xl md:text-2xl font-black tracking-tight leading-tight mb-1.5">
                  {item.title}
                </h2>
                <p className="text-sm text-foreground/60 leading-relaxed mb-4">{item.summary}</p>

                <div className="rounded-2xl border border-border bg-background/80 p-4">
                  <div className="text-[10px] font-black uppercase tracking-widest text-foreground/40 mb-3">
                    What’s new
                  </div>
                  <ul className="space-y-3">
                    {item.bullets.map((b) => (
                      <li key={b.label} className="text-sm text-foreground/80 leading-relaxed">
                        <span className="font-bold text-foreground">{b.label}:</span> {b.text}
                      </li>
                    ))}
                  </ul>
                </div>

                {item.footer && (
                  <p className="mt-4 text-xs text-foreground/45 italic leading-relaxed">{item.footer}</p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <SiteFooter />
      </ContentLayout>
    </div>
  );
}
